/* DOPA V59 — launch-safety + order-language guard.
   Current published products accept normal orders through Shopify. Products with
   extended fulfillment keep their 1–4 week dispatch disclosure in product details,
   cart data and order data; buttons use normal order language. */
(() => {
  const rootPath = window.Shopify?.routes?.root || '/';
  const canonicalHashes = Object.freeze({
    '#shop': '#shop-current',
    '#states': '#state-finder',
    '#gummies': '#state-rest',
    '#shots': '#dopa-next',
    '#state-004': '#state-fresh'
  });

  const rootUrl = (hash) => `${rootPath}${String(hash || '').replace(/^\//, '')}`;
  const normalizeAnchor = (anchor) => {
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const raw = anchor.getAttribute('href') || '';
    let url;
    try { url = new URL(raw, window.location.origin); } catch (_) { return; }
    if (url.origin !== window.location.origin) return;
    const path = url.pathname.replace(/\/+$/, '') || '/';
    const shopRoot = String(rootPath).replace(/\/+$/, '') || '/';
    if (path !== '/' && path !== shopRoot) return;
    const replacement = canonicalHashes[url.hash.toLowerCase()];
    if (!replacement) return;
    anchor.setAttribute('href', rootUrl(replacement));
  };

  const normalButtonLabel = (value) => {
    const raw = String(value || '').trim();
    const exact = {
      'PRE-ORDER DOPA DAY SYSTEM':'ORDER DOPA DAY SYSTEM',
      'PRE-ORDER DAY SYSTEM':'ORDER DAY SYSTEM',
      'PRE-ORDER DOPA SYSTEM':'ORDER DOPA SYSTEM',
      'PRE-ORDER SYSTEM':'ORDER SYSTEM',
      'PRE-ORDER IQ300 3 PACK':'ORDER IQ300 3 PACK',
      'PRE-ORDER 3 PACK':'ORDER 3 PACK',
      'PRE-ORDER DOPA MAX':'ORDER DOPA MAX',
      'PRE-ORDER IQ300':'ORDER IQ300',
      'PRE-ORDER KIK':'ORDER KIK',
      'PRE-ORDER RESET':'ORDER RESET',
      'PRE-ORDER FOCUS':'ADD FOCUS',
      'PRE-ORDER ENERGY':'ADD ENERGY',
      'PRE-ORDER REST':'ADD REST',
      'PRE-ORDER FRESH':'ADD FRESH',
      'PRE-ORDER NOW':'ORDER NOW',
      'PRE-ORDER':'ADD TO BAG'
    };
    if (exact[raw.toUpperCase()]) return exact[raw.toUpperCase()];
    if (/^\+\s*PRE-ORDER\b/i.test(raw)) return raw.replace(/^\+\s*PRE-ORDER\b/i, '+ ADD');
    if (/^PRE-ORDER\b/i.test(raw)) return raw.replace(/^PRE-ORDER\b/i, 'ORDER');
    return raw;
  };

  const normalStatusLabel = (value) => {
    const raw = String(value || '').trim();
    if (!/PRE-ORDER/i.test(raw)) return raw;
    if (/PRE-ORDER\s+OPEN/i.test(raw)) return 'ORDER NOW';
    if (/1\s*[–-]\s*4\s*WEEKS/i.test(raw)) return 'ORDER NOW · EST. DISPATCH 1–4 WEEKS';
    if (/PRE-ORDER\s+TIMING\s+STAYS\s+WITH\s+ITEM/i.test(raw)) return 'EST. DISPATCH TIMING STAYS WITH ITEM';
    if (/^PRE-ORDER$/i.test(raw)) return 'ORDER NOW · EST. DISPATCH 1–4 WEEKS';
    if (/·\s*PRE-ORDER/i.test(raw)) return raw.replace(/·\s*PRE-ORDER/ig, '· DISPATCH 1–4 WEEKS');
    return raw.replace(/PRE-ORDER/ig, 'ORDER NOW');
  };

  const shouldPreserveDisclosure = (node) => Boolean(node?.closest?.('.rte, details, .faq-page, [data-preserve-preorder-copy]'));

  const normalizeOrderLanguage = (root = document) => {
    const scope = root?.querySelectorAll ? root : document;

    scope.querySelectorAll('button, a.btn, [data-mobile-add]').forEach((node) => {
      if (shouldPreserveDisclosure(node)) return;
      if (node.getAttribute('data-dopa-ready-label')) {
        node.setAttribute('data-dopa-ready-label', normalButtonLabel(node.getAttribute('data-dopa-ready-label')));
      }
      const current = String(node.textContent || '').trim();
      const next = normalButtonLabel(current);
      if (next && next !== current) node.textContent = next;
    });

    const statusSelector = [
      '.d57-hero-product-top em', '.d57-product-meta em', '.d58-format-product-meta em',
      '.d58-state-card-top em', '.catalog-card-meta em', '.recommendation-card-copy em',
      '.d59-offer-price em', '.d59-order-status span', '.mobile-buy-mode', '.cart-preorder',
      '.dopa-aov-copy span', '.dopa-cart-checkout-trust span', '.kit-copy i',
      '[data-d57-result-status]'
    ].join(',');
    scope.querySelectorAll(statusSelector).forEach((node) => {
      if (shouldPreserveDisclosure(node)) return;
      const current = String(node.textContent || '').trim();
      const next = normalStatusLabel(current);
      if (next && next !== current) node.textContent = next;
    });

    scope.querySelectorAll('[data-d57-result][data-status]').forEach((node) => {
      const current = node.getAttribute('data-status') || '';
      const next = normalStatusLabel(current);
      if (next !== current) node.setAttribute('data-status', next);
    });
  };

  const normalizeState = (root = document) => {
    root.querySelectorAll?.('a[href]').forEach(normalizeAnchor);
    root.querySelectorAll?.('[data-dopa-state="night"], [data-d57-state="night"], [data-state-funnel="night"]').forEach((node) => {
      if (node.dataset.dopaState === 'night') node.dataset.dopaState = 'rest';
      if (node.dataset.d57State === 'night') node.dataset.d57State = 'rest';
      if (node.dataset.stateFunnel === 'night') node.dataset.stateFunnel = 'rest';
    });
  };

  const normalizeAll = (root = document) => {
    normalizeState(root);
    normalizeOrderLanguage(root);
  };

  normalizeAll(document);
  document.addEventListener('DOMContentLoaded', () => normalizeAll(document), { once:true });
  document.addEventListener('dopa:cart-rendered', () => normalizeAll(document));
  document.addEventListener('dopa:cart-updated', () => normalizeAll(document));
  document.addEventListener('dopa:cart-added', () => window.setTimeout(() => normalizeAll(document), 30));
  document.addEventListener('shopify:section:load', (event) => normalizeAll(event.target || document));

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('a[href]')) normalizeAnchor(node);
        normalizeAll(node);
      }
      if (mutation.type === 'characterData' && mutation.target?.parentElement) normalizeAll(mutation.target.parentElement);
    }
  });
  observer.observe(document.documentElement, { childList:true, subtree:true, characterData:true });
})();
